import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { UserDetails } from '../../types/schemas/abc';
import { 
  SidebarWrapper, 
  MetricGroup, 
  MetricLabel, 
  MetricScore, 
  MetricDescription, 
  SidebarWrapper2,
  DetailSidebarWrapper
} from '../../styles/search/search.style';
import { calculateComprehensiveMatch } from '../../utils/matchHelpers';
import { getTimezoneOffset, adjustAvailabilityForTimezone, calculateOverlapMinutes } from '../../utils/availability';


interface SimilaritySidebarProps {
  parent: UserDetails;
  tutor: UserDetails;
  isOpen: boolean;
  onOverflowChange: (isOverflowing: boolean) => void;
  dataState: 'open' | 'closed';
  topPosition: number | null
  isDetailedView?: boolean;
  isParentUser?: boolean;
}

export const SimilaritySidebar: React.FC<SimilaritySidebarProps> = ({ 
  parent, 
  tutor, 
  isOpen, 
  onOverflowChange, 
  dataState,
  topPosition, 
  isDetailedView=false,
  isParentUser=true
}) => {

  const sidebarRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const node = sidebarRef.current;
    if (!node) return;
  
    const update = () =>
      onOverflowChange(node.scrollHeight > node.clientHeight);
  
    // measure immediately
    update();
  
    // watch for later changes
    const ro = new ResizeObserver(update);
    ro.observe(node);
  
    return () => ro.disconnect();
  }, [onOverflowChange]);
  
  const { exactMatches, partialMatches, totalRequirements } = useMemo(
    () => calculateComprehensiveMatch(parent, tutor),
    [parent, tutor]
  );




  const scheduleOverlapHours = useMemo(() => {
    if (!parent.availability || !tutor.availability) return 0;
    const parentTz = parent.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tutorTz = tutor.timezone || parentTz;
    const offset = getTimezoneOffset(parentTz, tutorTz);
    const adjusted = adjustAvailabilityForTimezone(tutor.availability, offset);
    return Math.round(calculateOverlapMinutes(parent.availability, adjusted) / 60);
  }, [parent.availability, tutor.availability, parent.timezone, tutor.timezone]);
  
  const subjectMatchStatement = (
    <MetricGroup>
      <MetricLabel>Expertise Match</MetricLabel>
      <MetricScore>{exactMatches} / {totalRequirements}</MetricScore>
      <MetricDescription>
        {isParentUser
          ? `This tutor covers ${exactMatches} of your ${totalRequirements} required subject-levels.`
          : `You cover ${exactMatches} of this parent's ${totalRequirements} required subject-levels.`}
      </MetricDescription>
      {partialMatches > 0 && (
        <MetricDescription style={{ marginTop: '0.5rem', color: '#e6a700' }}>
          {isParentUser
            ? `Also teaches ${partialMatches} ${partialMatches === 1 ? 'subject' : 'subjects'} you need, but at a different level.`
            : `You also teach ${partialMatches} ${partialMatches === 1 ? 'subject' : 'subjects'} they need, but at a different level.`}
        </MetricDescription>
      )}
    </MetricGroup>
  );
  const sidebarContent = (
    <>

      {subjectMatchStatement }


      <MetricGroup>
        <MetricLabel>Schedule Overlap</MetricLabel>
        <MetricScore>{scheduleOverlapHours} hrs</MetricScore>
        <MetricDescription>
          Your general availability overlaps by {scheduleOverlapHours} hours per week.
        </MetricDescription>
      </MetricGroup>
    </>
  );


  if (isDetailedView) {
    return ( 
        <DetailSidebarWrapper ref={sidebarRef} data-state={dataState}>
            {sidebarContent}
        </DetailSidebarWrapper>
    )
  }

  return (
    <SidebarWrapper2 ref={sidebarRef} data-state={dataState}
    style={{ top: topPosition != null ? `${topPosition}px` : undefined }}

>
      {sidebarContent}
    </SidebarWrapper2>
  );
  
};